import { Button, cn, formatDate as formatUiDate, useTranslation } from '@hbs-us/ui';
import { Link, useNavigate } from '@tanstack/react-router';

import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { MoreMenu } from '../_ops/MoreMenu.js';
import { SkeletonRows } from '../_ops/SkeletonRows.js';
import { InvoiceStatusBadge } from './components/InvoiceStatusBadge.js';
import { InvoiceSummaryCards } from './components/InvoiceSummaryCards.js';
import { InvoicesEmptyState } from './components/InvoicesEmptyState.js';
import { InvoicesTable } from './components/InvoicesTable.js';
import type { InvoiceListItemDto, InvoiceListResponse } from './contractsMirror.js';
import type { InvoicesSearch } from './searchParams.js';

/**
 * The invoices list on the locked Velocity brand, uplifted to the P22 redesign
 * grammar (D-04 re-skin — functionality UNCHANGED). Page wrapper + header (title,
 * subtitle, archived toggle) + the 2-card summary stat grid + the invoices table
 * — or the empty state. The coarse endpoint owns all aggregation; this view only
 * renders the returned payload (no hook/endpoint change).
 *
 * Redesign grammar added here (UI-SPEC §4/§5), all presentation-only:
 * - **Responsive table→card**: the semantic `<table>` (`InvoicesTable`) shows at
 *   `md+`; below `md` the rows reflow to stacked label:value record cards so there
 *   is no horizontal scroll on phones.
 * - **Inline row actions**: each card exposes a `_ops/MoreMenu` row menu whose
 *   "View" item NAVIGATES to the deep-linkable detail route (no mutation — this
 *   list never writes; the GET-mutate footgun is avoided).
 * - **Skeleton loading**: an optional `loading` flag renders `_ops/SkeletonRows`
 *   shimmer rows; it defaults off so the existing Route render is unchanged.
 *
 * The archived toggle is a `Link` that flips `?show_archived` in the typed search
 * params, which drives exactly one query refetch. Exports stay via api-host GET
 * (no client renderer); the public pay link stays SSR (it is never re-implemented
 * in this authenticated SPA). Every color is a locked token (BRAND-06).
 */

export interface InvoicesViewProps {
  data: InvoiceListResponse;
  search: InvoicesSearch;
  /** Renders skeleton shimmer rows during a background refetch. Defaults off. */
  loading?: boolean;
}

export function InvoicesView({ data, search, loading = false }: InvoicesViewProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const fmt = useMoneyFormatters();
  const navigate = useNavigate();

  const invoices = data.invoices ?? [];
  const totalOutstanding = Number(data.totalOutstanding ?? 0);
  const totalOverdue = Number(data.totalOverdue ?? 0);
  const showArchived = Boolean(search.show_archived);
  const hasData = invoices.length > 0;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('invoices:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{t('invoices:subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/invoices"
            search={showArchived ? {} : { show_archived: true }}
            className={cn(
              'inline-flex h-11 items-center rounded-md border border-border bg-card px-4 font-medium text-foreground text-sm transition-colors',
              'hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
          >
            {showArchived ? t('invoices:actions.hideArchived') : t('invoices:actions.showArchived')}
          </Link>
          <Button asChild>
            <Link to="/invoices/new">{t('invoices:actions.create')}</Link>
          </Button>
        </div>
      </header>

      <InvoiceSummaryCards
        totalOutstanding={totalOutstanding}
        totalOverdue={totalOverdue}
        fmt={fmt}
      />

      {loading ? (
        <section className="rounded-md border border-border bg-card p-4">
          <SkeletonRows rows={6} columns={4} label={t('invoices:title')} />
        </section>
      ) : hasData ? (
        <>
          {/* Semantic table at md+; stacked record cards below md (no h-scroll on phones). */}
          <div className="hidden md:block">
            <InvoicesTable invoices={invoices} fmt={fmt} />
          </div>
          <ul className="grid gap-3 md:hidden">
            {invoices.map((inv) => (
              <li key={inv.id}>
                <InvoiceRecordCard
                  invoice={inv}
                  formatCurrency={fmt.formatCurrency}
                  locale={locale}
                  onView={() => navigate({ to: '/invoices/$id', params: { id: String(inv.id) } })}
                />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <InvoicesEmptyState showArchived={showArchived} />
      )}
    </div>
  );
}

/**
 * One invoice as a stacked label:value record card (UI-SPEC §5 responsive
 * table→card). The invoice number links to the detail route; the `_ops/MoreMenu`
 * exposes the "View" row action (navigation only — no list-side mutation). Same
 * data + locked tokens as `InvoicesTable`.
 */
function InvoiceRecordCard({
  invoice,
  formatCurrency,
  locale,
  onView,
}: {
  invoice: InvoiceListItemDto;
  formatCurrency: (value: number) => string;
  locale: string;
  onView: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <Link
          to="/invoices/$id"
          params={{ id: String(invoice.id) }}
          className="font-mono font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {invoice.invoiceNumber || t('invoices:table.unnamedInvoice', { id: invoice.id })}
        </Link>
        <MoreMenu
          label={t('invoices:actions.view')}
          items={[{ key: 'view', label: t('invoices:actions.view'), onSelect: onView }]}
        />
      </div>
      <div className="mt-0.5 text-muted-foreground text-xs">
        {invoice.clientName || t('invoices:table.noClient')}
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <InvoiceStatusBadge
          status={invoice.status}
          overdue={invoice.overdue}
          archived={Boolean(invoice.archivedAt)}
        />
        <span className="font-mono font-medium text-foreground text-sm">
          {formatCurrency(invoice.amount || 0)}
        </span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-border border-t pt-3">
        <div className="flex flex-col gap-0.5">
          <dt className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
            {t('invoices:table.issued')}
          </dt>
          <dd className="font-mono text-foreground text-sm">
            {invoice.dateIssued
              ? formatUiDate(invoice.dateIssued, locale, { dateStyle: 'medium' })
              : '—'}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
            {t('invoices:table.due')}
          </dt>
          <dd className="font-mono text-foreground text-sm">
            {invoice.dueDate ? formatUiDate(invoice.dueDate, locale, { dateStyle: 'medium' }) : '—'}
          </dd>
        </div>
      </dl>
    </div>
  );
}
